// src/components/layout/app-sidebar.jsx
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar
} from '@/components/ui/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage  } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'
import { useUserStores } from '@/hooks/visitorHooks'
import {
  LayoutDashboard,
  Store,
  Users,
  Settings,
  LogOut,
  Package,
  ShoppingCart,
  UserCircle,
  ChevronDown,
  Bell,
  User,
  Globe,
  Briefcase,
  Home,
  Megaphone,
  FileText,
  Plus,
  ArrowRight,
  Edit,
} from 'lucide-react'
import { StoreLogo } from '@/components/store/StoreLogo';
import { ThemeToggleMenuItem } from '@/components/theme-toggle';
import { useEffect } from 'react'

export function AppSidebar() {
  const {
    user,
    hasRole,
    logout,
    isAuthenticated,
    isImpersonating,
    impersonate,
    leaveImpersonation,
  } = useAuth()
  const navigate = useNavigate()

  const { setOpenMobile, isMobile } = useSidebar();

  const location = useLocation(); // You need to import this from '@tanstack/react-router'
  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [location, isMobile, setOpenMobile]); // The sidebar will close whenever the route changes

  // Always fetch when authenticated — works for both visitor and impersonated store sessions
  // because the backend returns the visitor's stores in both cases.
  const { data: userStores } = useUserStores({ enabled: !!isAuthenticated })
  const stores = userStores?.data || []

  // The currently active store (only populated when role is 'store', i.e. impersonating).
  const activeStore = hasRole('store') ? user?.store : null

  // Footer display values
  const displayName = activeStore ? activeStore.name : user?.name
  const avatarFallback = displayName
    ? displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U'

  const handleLogout = async () => {
    await logout()
    navigate({ to: '/login' })
  }

  const handleProfileClick = () => {
    navigate({ to: '/dashboard/profile' })
  }

  const handleStoreClick = async (store) => {
    await impersonate(store.user_id);
    const start = Date.now();
    const checkRole = () => {
      if (hasRole('store')) {
        navigate({ to: "/dashboard/infos", replace: true });
      } else if (Date.now() - start < 2000) {
        setTimeout(checkRole, 50);
      } else {
        // fallback – role not updated, try anyway
        navigate({ to: "/dashboard/infos", replace: true });
      }
    };
    checkRole();
  };

  const handleBackToVisitor = async () => {
    await leaveImpersonation()
    navigate({ to: "/dashboard/profile", replace: true })
  }

  const sections = [
    {
      label: 'المنصة',
      condition: hasRole('admin'),
      items: [
        { to: '/dashboard/places', icon: FileText, label: 'المدن والأحياء' },
        { to: '/dashboard/store-classes', icon: Globe, label: 'أنواع المتاجر' },
        {
          to: '/dashboard/products-specifications',
          icon: Bell,
          label: 'مميزات المنتجات',
        },
        {
          to: '/dashboard/profiles-pictures',
          icon: Settings,
          label: 'صور ملفات الزوار',
        },
      ],
    },
    {
      label: 'الإحصائيات',
      condition: hasRole('admin'),
      items: [
        {
          to: '/dashboard/statistics/places',
          icon: FileText,
          label: 'الأماكن',
        },
        { to: '/dashboard/statistics/stores', icon: Globe, label: 'المتاجر' },
        { to: '/dashboard/statistics/products', icon: Bell, label: 'المنتجات' },
        {
          to: '/dashboard/statistics/visitors',
          icon: Settings,
          label: 'الزوار',
        },
      ],
    },
    {
      label: 'طلبات الموافقة',
      condition: hasRole('admin'),
      items: [
        {
          to: '/dashboard/approval-requests/stores',
          icon: Globe,
          label: 'المتاجر',
        },
        {
          to: '/dashboard/approval-requests/products',
          icon: Bell,
          label: 'المنتجات',
        },
        {
          to: '/dashboard/approval-requests/plans',
          icon: Settings,
          label: 'العروض',
        },
      ],
    },
    {
      label: 'الإدارة',
      condition: hasRole('admin'),
      items: [
        { to: '/dashboard/admin/users', icon: Users, label: 'المستخدمين' },
        { to: '/dashboard/admin/settings', icon: Settings, label: 'الإعدادات' },
      ],
    },
    {
      // Store management — visible only when impersonating a store account.
      label: 'المتجر',
      condition: hasRole('store'),
      items: [
        { to: '/dashboard/infos', icon: ShoppingCart, label: 'معلومات المتجر' },
        { to: '/dashboard/catalogues', icon: Package, label: 'الكاتالوج' },
        { to: '/dashboard/products', icon: Package, label: 'قائمة المنتجات' },
        {
          to: '/dashboard/requests/products',
          icon: Edit,
          label: 'طلبات المنتجات',
        },
        { to: '/dashboard/followers', icon: Package, label: 'المتابعين' },
        { to: '/dashboard/subscription', icon: Package, label: 'الإشتراك' },
        {
          to: '/dashboard/payments-history',
          icon: Package,
          label: 'سجل المدفوعات',
        },
      ],
    },
    {
      // Visitor personal area — visible only when logged in as a visitor (not impersonating).
      label: 'المنصة',
      condition: hasRole('visitor'),
      items: [
        { to: '/dashboard/profile', icon: Briefcase, label: 'الملف الشخصي' },
        {
          to: '/dashboard/loved-products',
          icon: Briefcase,
          label: 'الإعجابات',
        },
        {
          to: '/dashboard/saved-products',
          icon: Megaphone,
          label: 'المحفوظات',
        },
        { to: '/dashboard/followed-stores', icon: Globe, label: 'من أتابع' },
        { to: '/dashboard/notifications', icon: Globe, label: 'الإشعارات' },
        { to: '/dashboard/create-store', icon: Globe, label: 'أنشى متجرك' },
        { to: '/dashboard/create-ad', icon: Globe, label: 'أعلن' },
      ],
    }
  ]

  return (
    <Sidebar dir="rtl">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-4 py-2">
          <Link to="/" className="flex items-center gap-2">
            <img src="/apple-touch-icon.png" alt="" className="h-7 w-7" />
            <span className="text-lg font-bold">سكيكيمة</span>
          </Link>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* الرئيسية — always visible */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link
                    to="/dashboard"
                    activeProps={{
                      className: 'bg-accent text-brand',
                    }}
                  >
                    <LayoutDashboard className="ml-2 h-4 w-4" />
                    <span>لوحة التحكم</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {sections.map((section) => {
          if (!section.condition) return null
          return (
            <Collapsible
              key={section.label}
              defaultOpen
              className="group/collapsible"
            >
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className="w-full flex items-center justify-between">
                    <span>{section.label}</span>
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {section.items.map((item) => (
                        <SidebarMenuItem key={item.to}>
                          <SidebarMenuButton asChild>
                            <Link
                              to={item.to}
                              activeProps={{ className: 'bg-accent text-brand' }}
                            >
                              <item.icon className="ml-2 h-4 w-4" />
                              <span>{item.label}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          )
        })}
      </SidebarContent>

      <SidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-start px-4 py-2 h-auto"
            >

<div className="h-8 w-8 rounded-full border border-border bg-muted flex-shrink-0 overflow-hidden">
  {activeStore ? (
    <StoreLogo store={activeStore} size="thumbnail" className="w-full h-full object-cover" />
  ) : (
    <img src={user?.avatar} alt={displayName} className="w-full h-full object-cover" />
  )}
</div>
              <div className="flex flex-col items-start text-right">
                <span className="text-sm font-medium">{displayName}</span>
                {isImpersonating && (
                  <span className="text-xs text-muted-foreground">
                    وضع المتجر
                  </span>
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" dir="rtl" className="w-64">
            <DropdownMenuLabel
              className="font-normal"
              onClick={() => {
                if (activeStore) {
                  navigate({ to: '/store/' + activeStore?.slug });
                } else {
                  navigate({ to: `/user/${user?.id}` });
                }
              }}
            >
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {displayName}
                </p>
                {isImpersonating && (
                  <p className="text-xs text-muted-foreground mt-1">
                    وضع المتجر
                  </p>
                )}
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            {isImpersonating ? (
              // Store mode menu
              <>
                <DropdownMenuItem
                  onClick={() => navigate({ to: '/dashboard/infos' })}
                >
                  <ShoppingCart className="ml-2 h-4 w-4" />
                  معلومات المتجر
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate({ to: '/dashboard/products' })}
                >
                  <Package className="ml-2 h-4 w-4" />
                  المنتجات
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleBackToVisitor}>
                  <User className="ml-2 h-4 w-4" />
                  العودة إلى الحساب الشخصي
                </DropdownMenuItem>
              </>
            ) : (
              // Visitor mode menu
              <>
                <DropdownMenuItem onClick={handleProfileClick}>
                  <User className="ml-2 h-4 w-4" />
                  الملف الشخصي
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate({ to: '/dashboard' })}
                >
                  <Settings className="ml-2 h-4 w-4" />
                  الإعدادات
                </DropdownMenuItem>
              </>
            )}

            {/* Store switcher — always visible as long as the visitor has stores */}
            {stores?.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>
                  {isImpersonating ? 'متاجري الأخرى' : 'متاجري'}
                </DropdownMenuLabel>
                {stores?.map((store) => (
                  <DropdownMenuItem
                    key={store?.id}
                    onClick={() => handleStoreClick(store)}
                    className={store?.id === activeStore?.id ? 'bg-accent' : ''}
                  >
                    <Store className="ml-2 h-4 w-4" />
                    <span className="flex-1">{store.name}</span>
                    {store.status === 'pending' && (
                      <Badge variant="outline" className="mr-2 text-xs">
                        قيد الانتظار
                      </Badge>
                    )}
                    {store?.id === activeStore?.id && (
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </DropdownMenuItem>
                ))}
              </>
            )}

            {/* Create store — visitor mode only */}
            {!isImpersonating && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-brand focus:text-brand"
                  onClick={() => navigate({ to: '/dashboard/create-store' })}
                >
                  <Plus className="ml-2 h-4 w-4 text-brand" />
                  إنشاء متجر جديد
                </DropdownMenuItem>
              </>
            )}

            <DropdownMenuSeparator />
            <ThemeToggleMenuItem />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="ml-2 h-4 w-4" />
              تسجيل الخروج
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
